import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { logger } from "@amzn/afxtaco-utilities";
import { getCurrentUser, signInWithRedirect } from "aws-amplify/auth";
import { Amplify } from "aws-amplify";
import store from "../redux/store";
import { config } from "../config";
import { FallbackRender } from "./components/FallbackRender";
import { configureAmplify } from "./auth/amplifyConfig";
import {
  deleteFromClientSTorage,
  getFromClientStorage,
} from "./auth/indexedDb/ClientStorage";
import { SIGNED_OUT_LOCATION } from "./auth/constants";
import { refreshSession } from "./auth/authUtils";
import { initializeRum } from "./monitoring";
import "./index.css";
import App from "./App";

/**
 * Cleans OAuth-related parameters from a URL to prevent them from
 * accumulating during redirect loops
 */
const cleanOAuthParams = (url: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("code");
    urlObj.searchParams.delete("state");
    urlObj.searchParams.delete("error");
    urlObj.searchParams.delete("error_description");
    return urlObj.toString();
  } catch (error) {
    logger.error({
      loggerMsg: "Error cleaning OAuth params from URL",
      error,
      url,
    });
    return url;
  }
};

/**
 * Checks if the current URL contains OAuth callback parameters
 */
const isOAuthCallback = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has("code") || params.has("error");
};

/**
 * Run before rendering the App
 */
export const bootstrap = async (): Promise<void> => {
  // Initialize web app config and Amplify auth configuration
  await config.initialize();
  await configureAmplify();

  // If we're returning from OAuth callback, let Amplify process it
  if (isOAuthCallback()) {
    logger.info({
      loggerMsg: "OAuth callback detected, rendering app to process callback",
    });

    // Debug: Check if Amplify is still configured before processing OAuth callback
    try {
      Amplify.getConfig();
    } catch (error) {
      logger.error({
        loggerMsg: "Error checking Amplify config before OAuth callback",
        error,
      });
    }

    // Render immediately - Amplify will handle the OAuth callback
    renderApp();
    return;
  }

  // Check if user is already authenticated
  try {
    await getCurrentUser();
    const rumConfig = config.getRumConfig();
    if (rumConfig) {
      initializeRum(rumConfig);
    }
    renderApp();
  } catch (error) {
    // User is not authenticated, initiate OAuth sign-in
    logger.info({
      loggerMsg: "User not authenticated, redirecting to sign in",
      error,
    });

    const signedOutLocation = await getFromClientStorage(SIGNED_OUT_LOCATION);
    if (signedOutLocation) {
      await deleteFromClientSTorage(SIGNED_OUT_LOCATION);
    }

    const currentLocation = signedOutLocation
      ? cleanOAuthParams(signedOutLocation)
      : cleanOAuthParams(window.location.href);

    await signInWithRedirect({
      provider: { custom: config.getAuthConfig().provider },
      customState: JSON.stringify({ startLocation: currentLocation }),
    }).then(async () => {
      await refreshSession().catch((error) =>
        logger.error({
          loggerMsg: "Error refreshing auth session",
          error,
        }),
      );
    });
  }
};

/**
 * Render the app
 */
function renderApp(): void {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <Provider store={store}>
          <ErrorBoundary FallbackComponent={FallbackRender}>
            <App />
          </ErrorBoundary>
        </Provider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

bootstrap().catch((err) => {
  logger.error({
    loggerMsg: "Unable to bootstrap app",
    err,
  });
});
